import { faArrowUpRightFromSquare, faFileLines } from '@fortawesome/pro-solid-svg-icons';
import { Box, Typography } from '@mui/material';

import { acceptInsuranceService } from '../../api/acceptInsurance';
import { getPpiDocument, PpiDocumentType } from '../../api/documentApi';
import Description from '../../components/Description/Description';
import { ErrorMessage } from '../../components/ErrorMessage';
import Icon from '../../components/Icon/Icon';
import Link from '../../components/Link/Link';
import ListField from '../../components/ListField/ListField';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import RadioField from '../../components/RadioField/RadioField';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { sanitizeHtml } from '../../helpers/sanitizeHtml';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useArnFromPath from '../../hooks/useArnFromPath';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import useGetConsentService from '../../hooks/useGetConsentService';
import useGetSelectedConsolidations from '../../hooks/useGetSelectedConsolidations';
import usePagesConfig from '../../hooks/usePagesConfig';
import usePpiConsentGate from '../../hooks/usePpiConsentGate';
import useSetBannerType from '../../hooks/useSetBannerType';
import useSettings from '../../hooks/useSettings';
import useTranslation from '../../hooks/useTranslation';
import { getApplicationState } from '../../store/application/application';
import { selectFieldStateByName } from '../../store/applicationform/applicationFormSelectors';

const PpiPageDK = () => {
    const dispatch = useAppDispatch();
    const translate = useTranslation();
    const { totalSections } = usePagesConfig();
    const { apiKey, proxyBaseUrl } = useSettings();
    const applicationReferenceNumber = useArnFromPath();
    useComponentTracker('PpiPage');
    const { consentDetails, isError, isLoading } = useGetConsentService('TERMS_PPI');
    const defaultPagesAction = useDefaultPagesAction();
    const { currentPage } = usePagesContext();

    const { hasConsentToAccept, canProceed, primaryConsent } = usePpiConsentGate(consentDetails, isLoading, isError);
    const value = useAppSelector(selectFieldStateByName)(primaryConsent?.name ?? '')?.value;
    const { selectedConsolidations } = useGetSelectedConsolidations();
    const isConsolidationLoan = (selectedConsolidations?.consolidations?.length ?? 0) > 0;

    useSetBannerType('bannerThree');

    const downloadDocument = (ppiDocumentType: PpiDocumentType) => {
        getPpiDocument(proxyBaseUrl, apiKey, applicationReferenceNumber!, ppiDocumentType);
    };

    const onAction = async () => {
        if (!applicationReferenceNumber) {
            return false;
        }

        if (!canProceed()) {
            return false;
        }

        await acceptInsuranceService(proxyBaseUrl, apiKey, applicationReferenceNumber, value === 'true');

        await dispatch(getApplicationState({ applicationReferenceNumber }));

        return true;
    };

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={defaultPagesAction}
                currentPage={currentPage}
                currentSection={3}
                numberOfSections={totalSections}
                numberOfPagesInSection={isConsolidationLoan ? 3 : 2}
                currentPageInSection={1}
                sectionTitle={translate('field.section.your-account')}
            />
            <Title text={translate('page.payment-protection-insurance.title')} />
            <Description text={translate('page.payment-protection-insurance.description')} />
            {consentDetails?.map((consentDetail) => (
                <Box key={consentDetail.name}>
                    <Box>
                        <ListField
                            name="paymentProtectionInsuranceConditions"
                            hideDivider={true}
                            items={[
                                {
                                    index: '1',
                                    icon: <Icon icon={faFileLines} />,
                                    label: (
                                        <Link onClick={() => downloadDocument('PRODUCT_FACT_SHEET')}>
                                            <Typography variant="body2">
                                                {translate(
                                                    'field.payment-protection-insurance.pdf.document-product-fact-sheet.label',
                                                )}
                                            </Typography>
                                        </Link>
                                    ),
                                    endIcon: <Icon icon={faArrowUpRightFromSquare} size="sm" />,
                                },
                                {
                                    index: '2',
                                    icon: <Icon icon={faFileLines} />,
                                    label: (
                                        <Link onClick={() => downloadDocument('PRE_AND_POST_PURCHASE')}>
                                            <Typography variant="body2">
                                                {translate(
                                                    'field.payment-protection-insurance.pdf.document-purchase-information.label',
                                                )}
                                            </Typography>
                                        </Link>
                                    ),
                                    endIcon: <Icon icon={faArrowUpRightFromSquare} size="sm" />,
                                },
                                {
                                    index: '3',
                                    icon: <Icon icon={faFileLines} />,
                                    label: (
                                        <Link onClick={() => downloadDocument('INSURANCE_TERMS')}>
                                            <Typography variant="body2">
                                                {translate(
                                                    'field.payment-protection-insurance.pdf.document-insurance-terms.label',
                                                )}
                                            </Typography>
                                        </Link>
                                    ),
                                    endIcon: <Icon icon={faArrowUpRightFromSquare} size="sm" />,
                                },
                                {
                                    index: '4',
                                    icon: <Icon icon={faFileLines} />,
                                    label: (
                                        <Link onClick={() => downloadDocument('INSURANCE_MEDIATION')}>
                                            <Typography variant="body2">
                                                {translate(
                                                    'field.payment-protection-insurance.pdf.document-insurance-information.label',
                                                )}
                                            </Typography>
                                        </Link>
                                    ),
                                    endIcon: <Icon icon={faArrowUpRightFromSquare} size="sm" />,
                                },
                            ]}
                        />
                    </Box>
                    <Box
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(consentDetail.information || '') }}
                        sx={{ mb: 3 }}
                    />
                    <RadioField
                        name={consentDetail.name}
                        options={[
                            { id: 'true', label: consentDetail.label },
                            {
                                id: 'false',
                                label: translate('field.payment-protection-insurance.option.no.label'),
                            },
                        ]}
                    />
                </Box>
            ))}
            {!isLoading && !hasConsentToAccept && (
                <ErrorMessage title={translate('page.error.title')} description={translate('page.error.description')} />
            )}
            <PagesFooter
                actions={['next']}
                onAction={onAction}
                currentSection={3}
                numberOfSections={totalSections}
                numberOfPagesInSection={isConsolidationLoan ? 3 : 2}
                currentPageInSection={1}
                sectionTitle={translate('field.section.your-account')}
            />
        </>
    );
};

export default PpiPageDK;
